import cx from "classnames";
import { useAtomValue } from "jotai/index";
import { type ReactNode, useCallback, useMemo } from "react";
import { canv } from "@/canv/global";
import { eventEmitter } from "@/eventEmitter";
import { generateColor } from "@/helpers/generateColor";
import { getInitials } from "@/helpers/getInitials";
import { UsersChevronIcon } from "@/icons";
import { usersAtom } from "@/interface/AppStore";
import { BottomBarItem } from "@/interface/BottomBar/BottomRightBar";
import styles from "@/interface/HeaderBar/RightBar/RightBar.module.scss";
import { isMobile } from "@/utils";

export const UsersList = ({ blackBackground }: { blackBackground?: boolean }) => {
    const { users, trackingSession } = useAtomValue(usersAtom);

    const showMultiLimit = useMemo(() => {
        return isMobile() ? 2 : 4;
    }, []);

    const tryView = useCallback((sessionUID: string, e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        canv.multiManager.tryViewSubscribe(sessionUID);
    }, []);

    const userMonets = useMemo(() => {
        const monets: ReactNode[] = [];
        const currentUserToken = canv.getInitDataProp("userToken");

        for (let i = 0; i < users.length && i < showMultiLimit; i++) {
            const user = users[i];

            monets.push(
                <div
                    key={`user-${user.sessionUID}`}
                    className={styles.barPanelRightUsersItem}
                    onClick={(e) => tryView(user.sessionUID, e)}
                >
                    <div
                        className={cx(
                            styles.barPanelRightUsersItemView,
                            trackingSession === user.sessionUID &&
                                styles.barPanelRightUsersItemIndicatorSelected,
                        )}
                        style={{ backgroundColor: user.color }}
                    >
                        {getInitials(user.name)}
                    </div>
                </div>,
            );
        }

        monets.push(
            <div
                key={`current-user-${currentUserToken}`}
                className={styles.barPanelRightUsersFirstItem}
            >
                <div
                    className={styles.barPanelRightUsersItemView}
                    style={{
                        backgroundColor: generateColor(currentUserToken),
                    }}
                >
                    {getInitials(canv.getInitDataProp("userName"))}
                </div>
            </div>,
        );

        return monets;
    }, [users, trackingSession, showMultiLimit, tryView]);

    const openUsersWindow = () => {
        canv.appInterface.usersWindow.show();
        eventEmitter.dispatch("header.button.active.name", {
            name: null,
        });
    };

    return (
        <BottomBarItem
            className={cx(styles.usersList, blackBackground ? styles.whiteChevron : "")}
            onClick={openUsersWindow}
        >
            <span className={styles.barPanelRightUsers}>{userMonets}</span>
            {users.length > showMultiLimit && (
                <div className="barPanelRightUsersItemViewMore">
                    +{users.length - showMultiLimit}
                </div>
            )}
            <UsersChevronIcon />
        </BottomBarItem>
    );
};
